import React, { useEffect, useState } from "react";
import { createAdminAuthorizedNoPayLeave } from "../../../service/leavesEmployeeApiService";
import { getAllEmployeesMethod } from "../../../service/ApiServices";
import Select from "react-select";
import PropTypes from "prop-types";
import { notification } from "antd";

const AddAuthorizedNoPayLeave = ({ handleCancel, onSuccess }) => {
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [reason, setReason] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentDate] = useState(new Date());
  const [viewMonth, setViewMonth] = useState(currentDate.getMonth());
  const [viewYear, setViewYear] = useState(currentDate.getFullYear());
  const [calendarType, setCalendarType] = useState(null);
  const [leaveDays, setLeaveDays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const showNotification = (type, message, description) => {
    api[type]({
      message,
      description,
      placement: 'topRight',
      duration: 4.5,
    });
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    // Generate leaveDays when startDate or endDate changes
    if (selectedStartDate && selectedEndDate) {
      const newLeaveDays = [];
      let currentDate = new Date(selectedStartDate);
      while (currentDate <= selectedEndDate) {
        newLeaveDays.push({
          date: formatDate(new Date(currentDate)),
          Day: "FULL DAY",
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      setLeaveDays(newLeaveDays);
    } else {
      setLeaveDays([]);
    }
  }, [selectedStartDate, selectedEndDate]);

  const fetchEmployees = async () => {
    try {
      await getAllEmployeesMethod({
        setEmployeeData: (data) => {
          setFilteredEmployees(data.data || []);
        },
        setLoading: () => {},
        params: { page: 1, limit: 1000 },
        search: "",
      });
    } catch (error) {
      console.error("Error fetching employees:", error);
      showNotification('error', 'Error', 'Failed to fetch employees.');
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();
    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    return [year, month, day].join("-");
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const isSelectedDate = (date, type) => {
    if (type === "start" && selectedStartDate) {
      return date.getTime() === selectedStartDate.getTime();
    }
    if (type === "end" && selectedEndDate) {
      return date.getTime() === selectedEndDate.getTime();
    }
    return false;
  };

  const handleDateClick = (date, type) => {
    if (type === "start") {
      setSelectedStartDate(date);
    } else if (type === "end") {
      setSelectedEndDate(date);
    }
    setCalendarType(null);
  };

  const goToPreviousMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const renderCalendar = (type) => {
    const daysInMonth = getDaysInMonth(viewMonth, viewYear);
    const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewYear, viewMonth, day);
      const isSelected = isSelectedDate(date, type);
      const isToday = date.toDateString() === new Date().toDateString();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? "selected" : ""} ${isToday ? "today" : ""}`}
          onClick={() => handleDateClick(date, type)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const handleRemoveDate = (dateToRemove) => {
    setLeaveDays(leaveDays.filter((day) => day.date !== dateToRemove));
  };

  const resetForm = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setReason("");
    setSelectedEmployee(null);
    setLeaveDays([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      showNotification('error', 'Error', 'User data not found. Please log in again.');
      setIsLoading(false);
      return;
    }

    if (!selectedEmployee) {
      showNotification('warning', 'Required Fields Missing', 'Please select an employee.');
      setIsLoading(false);
      return;
    }

    if (!selectedStartDate) {
      showNotification('warning', 'Required Fields Missing', 'Please select the start date.');
      setIsLoading(false);
      return;
    }

    if (!selectedEndDate) {
      showNotification('warning', 'Required Fields Missing', 'Please select the end date.');
      setIsLoading(false);
      return;
    }

    if (selectedEndDate < selectedStartDate) {
      showNotification('warning', 'Invalid Date Range', 'End date cannot be before start date.');
      setIsLoading(false);
      return;
    }

    if (leaveDays.length === 0) {
      showNotification('warning', 'No Leave Days', 'At least one leave day is required.');
      setIsLoading(false);
      return;
    }

    const startDateFormatted = formatDate(selectedStartDate);
    const endDateFormatted = formatDate(selectedEndDate);

    const leaveData = {
      employeeId: selectedEmployee.value,
      adminUserId: user.data._id,
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      reason: reason || "Authorized No Pay Leave",
      leaveDays,
    };

    try {
      const response = await createAdminAuthorizedNoPayLeave(leaveData);

      if (response.status === "success") {
        showNotification('success', 'Authorized No Pay Leave Submitted', 'Authorized No Pay Leave has been submitted successfully for the selected employee!');
        resetForm();
        onSuccess();
        handleCancel();
      } else {
        showNotification('error', 'Error', response.message || 'Failed to submit Authorized No Pay Leave.');
      }
    } catch (error) {
      console.error("Failed to submit Authorized No Pay Leave:", error);
      showNotification('error', 'Error', error.response?.data?.message || 'An error occurred while submitting the Authorized No Pay Leave.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {contextHolder}
      <div className="page-wrapper-new p-0">
        <div className="modal-content">
          <div className="modal-header border-0 custom-modal-header-leave">
            <h4>Apply Authorized No Pay Leave</h4>
          </div>
          <div className="modal-body custom-modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-12">
                  <div className="mb-3 summer-description-box mb-0">
                    <label>Select Employee</label>
                    <Select
                      className="select"
                      value={selectedEmployee}
                      options={filteredEmployees.map((emp) => ({
                        value: emp._id,
                        label: emp.full_name || `${emp.first_name} ${emp.last_name}`,
                      }))}
                      placeholder="Select Employee"
                      onChange={setSelectedEmployee}
                      isClearable
                    />
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="mb-3 summer-description-box mb-0">
                    <label>Start Date</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedStartDate ? formatDate(selectedStartDate) : ""}
                      onClick={() => setCalendarType("start")}
                      placeholder="Select Start Date"
                      readOnly
                    />
                    {calendarType === "start" && (
                      <div className="calendar-wrapper">
                        <div className="calendar-header">
                          <div className="calendar-month-year">
                            {months[viewMonth]} {viewYear}
                          </div>
                          <div className="calendar-nav">
                            <button type="button" onClick={goToPreviousMonth}>
                              ←
                            </button>
                            <button type="button" onClick={goToNextMonth}>
                              →
                            </button>
                          </div>
                        </div>
                        <div className="calendar-grid">
                          {weekdays.map((day) => (
                            <div key={day} className="calendar-weekday">
                              {day}
                            </div>
                          ))}
                          {renderCalendar("start")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="mb-3 summer-description-box mb-0">
                    <label>End Date</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedEndDate ? formatDate(selectedEndDate) : ""}
                      onClick={() => setCalendarType("end")}
                      placeholder="Select End Date"
                      readOnly
                    />
                    {calendarType === "end" && (
                      <div className="calendar-wrapper">
                        <div className="calendar-header">
                          <div className="calendar-month-year">
                            {months[viewMonth]} {viewYear}
                          </div>
                          <div className="calendar-nav">
                            <button type="button" onClick={goToPreviousMonth}>
                              ←
                            </button>
                            <button type="button" onClick={goToNextMonth}>
                              →
                            </button>
                          </div>
                        </div>
                        <div className="calendar-grid">
                          {weekdays.map((day) => (
                            <div key={day} className="calendar-weekday">
                              {day}
                            </div>
                          ))}
                          {renderCalendar("end")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="mb-3 summer-description-box mb-0">
                    <label>Leave Days</label>
                    <div className="leave-days-list">
                      {leaveDays.length > 0 ? (
                        leaveDays.map((day, index) => (
                          <div key={index} className="leave-day-item">
                            {day.date} ({day.Day})
                            <button
                              type="button"
                              className="btn btn-danger btn-sm ms-2"
                              onClick={() => handleRemoveDate(day.date)}
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      ) : (
                        <p>No leave days selected.</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="mb-3 summer-description-box mb-0">
                    <label>Reason</label>
                    <input
                      type="text"
                      value={reason}
                      className="form-control"
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="Enter reason (optional)"
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  className="btn btn-reset me-2"
                  onClick={resetForm}
                >
                  Reset
                </button>
                <div className="page-btn">
                  <button
                    className="btn btn-submit"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Submitting..." : "Apply Authorized No Pay Leave"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

AddAuthorizedNoPayLeave.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddAuthorizedNoPayLeave;
